import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, formatErr } from "@/lib/api";
import { AREA_UNITS, BHK_OPTIONS, FACINGS, LISTING_TYPES, PROPERTY_TYPES, PROP_STATUSES, UNIT_TO_SQFT, canSeeOwner } from "@/lib/constants";
import { inr, inrCompact } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useAssignableOptions } from "@/hooks/useData";
import { Field, NSelect, PageHeader, Spinner } from "@/components/common";
import { DuplicateDialog } from "@/components/Dialogs";
import FileUploader from "@/components/properties/FileUploader";
import DatePicker from "@/components/DatePicker";
import { Section } from "@/pages/LeadFormPage";

const EMPTY = {
  listing_type: "Sale", property_type: "Flat", project: "", sector: "", city: "", address: "", maps_link: "", area: "", area_unit: "sq ft",
  bhk: "", floor: "", facing: "", price: "", negotiable: false, ownership: "", possession: "", possession_date: "", owner_name: "",
  owner_phone: "", co_broker: false, photos: [], documents: [], status: "Available", assigned_to: "", notes: "",
};
const num = (v) => (v === "" || v == null ? null : Number(v));

export default function PropertyFormPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const assignOpts = useAssignableOptions();
  const showOwner = canSeeOwner(user);
  const [f, setF] = useState({ ...EMPTY, assigned_to: user.id });
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);
  const { data: existing, isLoading } = useQuery({ queryKey: ["property", id], queryFn: async () => (await api.get(`/properties/${id}`)).data, enabled: !!id });

  useEffect(() => {
    if (existing) setF({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).filter((k) => existing[k] != null).map((k) => [k, existing[k]])) });
  }, [existing]);

  const s = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const ev = (k) => (e) => s(k)(e.target.value);
  const sqft = (Number(f.area) || 0) * UNIT_TO_SQFT[f.area_unit];
  const ppsf = sqft && Number(f.price) ? Number(f.price) / sqft : null;
  const isReq = f.listing_type === "Purchase requirement";

  const save = async (force = false) => {
    setBusy(true);
    const body = { ...f, area: num(f.area), price: num(f.price), assigned_to: f.assigned_to || null };
    try {
      const { data } = id ? await api.put(`/properties/${id}`, body, { params: { force } }) : await api.post("/properties", body, { params: { force } });
      toast.success(id ? "Property updated" : "Property added");
      qc.invalidateQueries({ queryKey: ["properties"] });
      qc.invalidateQueries({ queryKey: ["property", data.id] });
      qc.invalidateQueries({ queryKey: ["prop-cities"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      nav(`/properties/${data.id}`, { replace: true });
    } catch (e) {
      if (e.response?.status === 409) setDup(e.response.data.detail);
      else toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (id && isLoading) return <Spinner />;
  return (
    <form onSubmit={(e) => { e.preventDefault(); save(); }} className="space-y-6" data-testid="property-form">
      <Link to={id ? `/properties/${id}` : "/properties"} className="inline-flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" />Back</Link>
      <PageHeader title={id ? "Edit property" : "Add property"} testid="property-form-title" />
      <Section title="Listing">
        <Field label="Listing type"><NSelect testid="property-listing-type-select" value={f.listing_type} onChange={s("listing_type")} options={LISTING_TYPES} /></Field>
        <Field label="Property type"><NSelect testid="property-type-select" value={f.property_type} onChange={s("property_type")} options={PROPERTY_TYPES} /></Field>
        <Field label="Status"><NSelect testid="property-status-select" value={f.status} onChange={s("status")} options={PROP_STATUSES} /></Field>
        <Field label="Project / society"><Input data-testid="property-project-input" value={f.project} onChange={ev("project")} /></Field>
        <Field label="Sector"><Input data-testid="property-sector-input" value={f.sector} onChange={ev("sector")} /></Field>
        <Field label="City"><Input data-testid="property-city-input" value={f.city} onChange={ev("city")} placeholder="Mohali" /></Field>
        <Field label="Address" className="sm:col-span-2"><Input data-testid="property-address-input" value={f.address} onChange={ev("address")} /></Field>
        <Field label="Google Maps link"><Input data-testid="property-maps-input" type="url" value={f.maps_link} onChange={ev("maps_link")} placeholder="https://maps.google.com/…" /></Field>
      </Section>
      <Section title="Size & price">
        <Field label="Area"><Input type="number" min="0" step="any" data-testid="property-area-input" value={f.area} onChange={ev("area")} /></Field>
        <Field label="Area unit"><NSelect testid="property-area-unit-select" value={f.area_unit} onChange={s("area_unit")} options={AREA_UNITS} /></Field>
        <Field label="BHK"><NSelect testid="property-bhk-select" value={f.bhk} onChange={s("bhk")} placeholder="—" options={BHK_OPTIONS} /></Field>
        <Field label={f.listing_type === "Rent" ? "Monthly rent (₹)" : isReq ? "Expected price / budget (₹)" : "Price (₹)"}>
          <Input type="number" min="0" data-testid="property-price-input" value={f.price} onChange={ev("price")} />
          {f.price && <span className="num text-xs text-muted-foreground">{inrCompact(f.price)}</span>}
        </Field>
        <Field label="Price per sq ft (auto)"><div className="num flex h-10 items-center rounded-lg border bg-secondary/50 px-3 text-sm" data-testid="property-ppsf-display">{ppsf ? `${inr(ppsf)}/sq ft` : "—"}</div></Field>
        <Field label="Negotiable"><div className="flex h-10 items-center"><Switch checked={f.negotiable} onCheckedChange={s("negotiable")} data-testid="property-negotiable-switch" /></div></Field>
        <Field label="Floor"><Input data-testid="property-floor-input" value={f.floor} onChange={ev("floor")} /></Field>
        <Field label="Facing"><NSelect testid="property-facing-select" value={f.facing} onChange={s("facing")} placeholder="—" options={FACINGS} /></Field>
        <Field label="Ownership"><NSelect testid="property-ownership-select" value={f.ownership} onChange={s("ownership")} placeholder="—" options={["Freehold", "Leasehold"]} /></Field>
        <Field label="Possession"><NSelect testid="property-possession-select" value={f.possession} onChange={s("possession")} placeholder="—" options={["Ready", "Under construction"]} /></Field>
        {f.possession === "Under construction" && <Field label="Expected possession"><DatePicker testid="property-possession-date-input" value={f.possession_date} onChange={s("possession_date")} /></Field>}
      </Section>
      <Section title="Source & team">
        {showOwner && <Field label="Owner / source name"><Input data-testid="property-owner-name-input" value={f.owner_name} onChange={ev("owner_name")} /></Field>}
        {showOwner && <Field label="Owner phone" hint="Never included in shared text"><Input inputMode="tel" data-testid="property-owner-phone-input" value={f.owner_phone} onChange={ev("owner_phone")} /></Field>}
        <Field label="Co-broker involved"><div className="flex h-10 items-center"><Switch checked={f.co_broker} onCheckedChange={s("co_broker")} data-testid="property-cobroker-switch" /></div></Field>
        <Field label="Assigned to"><NSelect testid="property-assigned-select" value={f.assigned_to} onChange={s("assigned_to")} placeholder="Unassigned" options={assignOpts} /></Field>
        <Field label="Notes" className="sm:col-span-2 lg:col-span-3"><Textarea data-testid="property-notes-input" value={f.notes} onChange={ev("notes")} rows={3} /></Field>
      </Section>
      <section className="space-y-5 rounded-2xl border bg-card p-5">
        <div>
          <h3 className="mb-3 font-display font-bold">Photos</h3>
          <FileUploader value={f.photos} onChange={s("photos")} kind="property_photo" accept="image/*" label="Add photos" testid="property-photos" image />
        </div>
        <div>
          <h3 className="mb-3 font-display font-bold">Documents (PDF)</h3>
          <FileUploader value={f.documents} onChange={s("documents")} kind="property_doc" accept="application/pdf,image/*" label="Add PDF" testid="property-docs" />
        </div>
      </section>
      <div className="sticky bottom-20 z-10 flex justify-end gap-2 md:bottom-4">
        <Button type="button" variant="outline" onClick={() => nav(-1)} data-testid="property-cancel-button">Cancel</Button>
        <Button type="submit" disabled={busy} className="shadow-lg" data-testid="property-save-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{id ? "Save changes" : "Add property"}</Button>
      </div>
      <DuplicateDialog dup={dup} basePath="/properties" onCancel={() => setDup(null)} onConfirm={() => { setDup(null); save(true); }}
        render={(d) => <><span className="font-semibold">{d.project}</span> · {[d.sector, d.city].filter(Boolean).join(", ")} · {d.listing_type} · {d.status}</>} />
    </form>
  );
}
